import { useCallback, useEffect, useMemo, useState } from 'react'
import * as echarts from 'echarts'
import type { Time } from 'lightweight-charts'
import type { ScreenerOIDataPoint, ScreenerTickerData } from '@/types'
import { MergedOIChart } from './MergedOIChart'
import { OIPanel } from './OIPanel'
import { PriceLineChart } from './PriceLineChart'
import { exchangeColor, SCREENER_CHART_GROUP } from './chartUtils'
import { useExchangeChartOrder } from './useExchangeChartOrder'
import { useExchangeChartReordering } from './useExchangeChartReordering'
import { useChartCollapsePreference } from './useChartCollapsePreference'
import { ResizableChart } from './ResizableChart'
import { CVDChart } from './CVDChart'
import { ChartPaneFrame } from './ChartPaneFrame'
import { DeferredChartPane } from './DeferredChartPane'
import { FEATURE_SCREENER_TRADES_ENABLED } from '@/lib/featureFlags'
import type { ChartAlertControls } from './ChartLevelAlerts'
import type { ScreenerMetric } from '@/lib/api'
import type { RulerExchangeSeries } from './ChartRuler'
import { useChartLayoutPreferences, type ChartLayoutPreferenceControls } from '@/hooks/useChartLayoutPreferences'

export type ViewMode = 'merged' | 'separate'

interface ChartLayoutManagerProps {
  tickerData: ScreenerTickerData | null
  symbol: string
  mode: ViewMode
  selectedExchanges: string[]
  priceExchange: string
  interval: string
  orderStorageKey: string
  alertControls?: ChartAlertControls
  layoutPreferences?: ChartLayoutPreferenceControls
  oiLineMode?: boolean
  onMetricsChange?: (metrics: ScreenerMetric[]) => void
}
export function ChartLayoutManager(props: ChartLayoutManagerProps) {
  return props.layoutPreferences
    ? <ChartLayoutContent {...props} layoutPreferences={props.layoutPreferences} />
    : <LocalChartLayoutManager {...props} />
}

function LocalChartLayoutManager(props: ChartLayoutManagerProps) {
  const layout = useChartLayoutPreferences(props.orderStorageKey)
  return <ChartLayoutContent {...props} layoutPreferences={layout.controls} />
}

function chartLabel(id: string) {
  if (id === 'price') return 'price'
  if (id === 'merged-oi') return 'merged OI'
  const [metric, exchange] = id.split(':')
  return `${exchange} ${metric.toUpperCase()}`
}

function ChartLayoutContent({
  tickerData,
  symbol,
  mode,
  selectedExchanges,
  priceExchange,
  interval,
  orderStorageKey,
  alertControls,
  layoutPreferences,
  oiLineMode = true,
  onMetricsChange,
}: ChartLayoutManagerProps & { layoutPreferences: ChartLayoutPreferenceControls }) {
  const [syncedTime, setSyncedTime] = useState<Time | null>(null)
  const { isCollapsed, toggleChart } = useChartCollapsePreference(orderStorageKey, {
    collapsed: layoutPreferences.value.collapsed, onChange: layoutPreferences.setCollapsed,
  })
  const paneHeight = (id: string, defaultHeight: number) => ({
    height: layoutPreferences.value.heights[id] ?? defaultHeight,
    onHeightChange: (height: number) => layoutPreferences.setHeight(id, height),
  })
  const handleCrosshairMove = useCallback((time: Time | null) => setSyncedTime(time), [])

  useEffect(() => {
    echarts.connect(SCREENER_CHART_GROUP)
    return () => echarts.disconnect(SCREENER_CHART_GROUP)
  }, [])

  const oiExchanges = useMemo(() => {
    if (!tickerData?.exchanges) return []
    return Object.entries(tickerData.exchanges)
      .filter(([, data]) => tickerData.history_window != null || (data.oi_history?.length || 0) > 0)
      .map(([exchange]) => exchange)
      .sort()
  }, [tickerData])

  const visibleExchanges = useMemo(() => {
    const selected = new Set(selectedExchanges)
    return oiExchanges.filter((exchange) => selected.has(exchange))
  }, [oiExchanges, selectedExchanges])

  const cvdExchanges = useMemo(() => [...new Set([
    ...Object.entries(tickerData?.exchanges || {})
      .filter(([, data]) => data.cvd_history !== undefined || data.cvd_error != null).map(([exchange]) => exchange),
    // Keep collapsed panes discoverable when their metric is omitted by reads.
    ...(FEATURE_SCREENER_TRADES_ENABLED ? selectedExchanges.filter(exchange => tickerData?.exchanges[exchange]) : []),
  ])].sort(), [tickerData, selectedExchanges])
  const chartExchanges = useMemo(() => [...new Set([...oiExchanges, ...cvdExchanges])], [oiExchanges, cvdExchanges])
  const chartIds = useMemo(() => [
    'price', 'merged-oi', ...oiExchanges.map((exchange) => `oi:${exchange}`), ...cvdExchanges.map((exchange) => `cvd:${exchange}`),
  ], [oiExchanges, cvdExchanges])
  const { orderedExchanges: orderedChartIds, moveExchange: moveChart } = useExchangeChartOrder(chartIds, orderStorageKey, {
    order: layoutPreferences.value.order, onChange: layoutPreferences.setOrder,
  })
  const visibleChartIds = orderedChartIds.filter((id) => {
    if (id === 'price') return true
    if (id === 'merged-oi') return mode === 'merged' && visibleExchanges.length > 0
    if (id.startsWith('oi:')) return mode === 'separate' && visibleExchanges.includes(id.slice(3))
    return FEATURE_SCREENER_TRADES_ENABLED && selectedExchanges.includes(id.slice(4))
  })
  const neededMetrics: ScreenerMetric[] = []
  if (!isCollapsed('price')) neededMetrics.push('price')
  if (selectedExchanges.some(exchange => !isCollapsed(mode === 'merged' ? 'merged-oi' : `oi:${exchange}`))) {
    neededMetrics.push('oi')
    if (FEATURE_SCREENER_TRADES_ENABLED) neededMetrics.push('volume')
  }
  if (FEATURE_SCREENER_TRADES_ENABLED && selectedExchanges.some(exchange => !isCollapsed(`cvd:${exchange}`))) neededMetrics.push('cvd')
  if (selectedExchanges.length) neededMetrics.push('funding') // Header badges remain visible when collapsed.
  const metricKey = neededMetrics.sort().join(',')
  useEffect(() => {
    onMetricsChange?.(metricKey ? metricKey.split(',') as ScreenerMetric[] : [])
  }, [metricKey, onMetricsChange])
  const reorderContext = JSON.stringify([symbol, mode, orderStorageKey])
  const reordering = useExchangeChartReordering(visibleChartIds, moveChart, reorderContext, chartLabel)

  const effectivePriceExchange = useMemo(() => {
    if (priceExchange && tickerData?.exchanges?.[priceExchange]) return priceExchange
    return visibleExchanges[0] || chartExchanges[0] || ''
  }, [priceExchange, tickerData, visibleExchanges, chartExchanges])

  const seriesByExchange = useMemo(() => {
    const result: Record<string, ScreenerOIDataPoint[]> = {}
    for (const exchange of visibleExchanges) {
      result[exchange] = tickerData?.exchanges?.[exchange]?.oi_history || []
    }
    return result
  }, [tickerData, visibleExchanges])

  const rulerExchanges = useMemo<RulerExchangeSeries[]>(() => [...new Set(selectedExchanges)].map((exchange, index) => {
    const data = tickerData?.exchanges?.[exchange]
    const visibleIndex = visibleExchanges.indexOf(exchange)
    return {
      id: exchange,
      label: exchange,
      color: exchangeColor(visibleIndex >= 0 ? visibleIndex : index),
      oiData: data?.oi_history || [],
      candles: data?.candles || [],
      currentPrice: data?.last_price,
    }
  }), [selectedExchanges, tickerData, visibleExchanges])

  const fillThrough = useMemo(() => {
    if (tickerData?.oi_history_until != null) return tickerData.oi_history_until
    // Older servers have no query watermark. Align exchanges only to received
    // chart history, never to the browser clock or unrelated ticker prices.
    const timestamps = Object.values(tickerData?.exchanges || {}).flatMap((data) =>
      (data.oi_history || []).map((point) => point.timestamp).filter(Number.isFinite))
    return timestamps.length > 0 ? timestamps.reduce((latest, value) => Math.max(latest, value), timestamps[0]) : null
  }, [tickerData])

  if (!tickerData) {
    return <div role="status" className="flex h-96 items-center justify-center text-sm text-slate-500">Loading charts for {symbol}…</div>
  }

  if (chartExchanges.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center rounded-xl border border-dashed border-slate-800 bg-slate-950/30 text-sm text-slate-500">
        No stored chart history is available for {symbol}.
      </div>
    )
  }

  const priceData = effectivePriceExchange ? tickerData.exchanges[effectivePriceExchange] : null

  const renderPane = (id: string) => {
    if (id === 'price') return (
      <ResizableChart
        {...paneHeight('price', 330)}
        storageKey={`${orderStorageKey}:height:price`}
        label="price chart" defaultHeight={330} minHeight={180} collapsed={isCollapsed('price')}
      >
        {(height) => (
          <PriceLineChart
            height={height}
            collapsed={isCollapsed('price')}
            onToggleCollapse={() => toggleChart('price')}
            rulerExchanges={rulerExchanges}
            alertControls={alertControls}
            key={`${symbol}:${effectivePriceExchange}`}
            oiData={priceData?.oi_history || []}
            candles={priceData?.candles || []}
            tradeHistory={priceData?.trade_price_history}
            candleSource={priceData?.candle_source}
            candleError={priceData?.candle_error}
            quoteCurrency={priceData?.candle_quote_currency}
            currentPrice={priceData?.last_price}
            market={priceData?.market === 'spot' ? 'spot' : 'futures'}
            candleInterval={priceData?.candle_interval ?? tickerData.price_interval}
            symbol={symbol}
            exchange={effectivePriceExchange}
            interval={interval}
            chartGroup={SCREENER_CHART_GROUP}
            onCrosshairMove={handleCrosshairMove}
            syncedTime={syncedTime}
          />
        )}
      </ResizableChart>
    )
    if (id === 'merged-oi') return (
      <ResizableChart
        {...paneHeight('merged-oi', 440)}
        storageKey={`${orderStorageKey}:height:merged-oi`}
        label="merged OI chart" defaultHeight={440} minHeight={240} collapsed={isCollapsed('merged-oi')}
      >
        {(height) => (
          <MergedOIChart
            lineMode={oiLineMode}
            height={height}
            collapsed={isCollapsed('merged-oi')}
            onToggleCollapse={() => toggleChart('merged-oi')}
            alertControls={alertControls}
            symbol={symbol}
            key={`${symbol}:${visibleExchanges.join(',')}`}
            seriesByExchange={seriesByExchange}
            exchangeData={tickerData.exchanges}
            candleInterval={tickerData.price_interval}
            interval={interval}
            fillThrough={fillThrough}
            chartGroup={SCREENER_CHART_GROUP}
            onCrosshairMove={handleCrosshairMove}
            syncedTime={syncedTime}
          />
        )}
      </ResizableChart>
    )
    const exchange = id.slice(id.indexOf(':') + 1)
    const data = tickerData.exchanges[exchange]
    if (id.startsWith('oi:')) return (
      <ResizableChart
        {...paneHeight(`oi:${exchange}`, 145)}
        storageKey={`${orderStorageKey}:height:oi:${exchange}`}
        label={`${exchange} OI chart`} defaultHeight={145} minHeight={120} collapsed={isCollapsed(`oi:${exchange}`)}
      >
        {(height) => (
          <OIPanel
            key={`${symbol}:${exchange}`}
            lineMode={oiLineMode}
            height={height}
            collapsed={isCollapsed(`oi:${exchange}`)}
            onToggleCollapse={() => toggleChart(`oi:${exchange}`)}
            alertControls={alertControls}
            symbol={symbol}
            oiData={data.oi_history || []}
            candles={data.candles || []}
            currentPrice={data.last_price}
            candleInterval={tickerData.price_interval}
            currentOI={data.oi_current}
            fundingData={data}
            volumeHistory={data.volume_history}
            volumeInterval={data.volume_interval}
            exchange={exchange}
            interval={interval}
            fillThrough={fillThrough}
            color={exchangeColor(visibleExchanges.indexOf(exchange))}
            chartGroup={SCREENER_CHART_GROUP}
            onCrosshairMove={handleCrosshairMove}
            syncedTime={syncedTime}
            showXAxis
          />
        )}
      </ResizableChart>
    )
    return (
      <ResizableChart
        {...paneHeight(`cvd:${exchange}`, 200)}
        storageKey={`${orderStorageKey}:height:cvd:${exchange}`}
        label={`${exchange} CVD chart`} defaultHeight={200} minHeight={140}
        collapsed={isCollapsed(`cvd:${exchange}`)}>
        {(height) => (
          <CVDChart
            key={`${symbol}:${exchange}`}
            symbol={symbol} exchange={exchange}
            candles={data.cvd_history || []}
            interval={data.cvd_interval || interval}
            unit={data.cvd_unit || 'coins'}
            baselineTimestamp={data.cvd_baseline_ts}
            hasGaps={data.cvd_has_gaps}
            error={data.cvd_error}
            loading={tickerData.pending_metrics?.includes('cvd')}
            height={height}
            collapsed={isCollapsed(`cvd:${exchange}`)}
            onToggleCollapse={() => toggleChart(`cvd:${exchange}`)}
            chartGroup={SCREENER_CHART_GROUP}
            onCrosshairMove={handleCrosshairMove}
            syncedTime={syncedTime}
          />
        )}
      </ResizableChart>
    )
  }

  return (
    <div className="screener-chart-layout w-full min-w-0">
      {Object.values(layoutPreferences.value.widths).some((width) => width < 100) && (
        <div className="mb-2 flex justify-end px-1">
          <button type="button" className="rounded border border-slate-700 px-2 py-1 text-[10px] text-slate-400 hover:text-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-300"
            title="Restore all chart widths to 100%, including hidden panes"
            onClick={() => Object.keys(layoutPreferences.value.widths).forEach((id) => layoutPreferences.setWidth(id, 100))}>
            Full-width rows
          </button>
        </div>
      )}
      <div className="mb-2 px-1 text-[10px] text-slate-500">Drag a pane handle or use its arrows to arrange charts · Drag the right edge to resize width and the bottom handle to resize height · Double-click a resize handle to reset · Charts stack in narrow workspaces</div>
      <div className="mb-2 px-1 text-[10px] text-slate-500">Double-click a price or OI graph to set a personal alert · Manage levels and delivery in My alerts · Drag right-axis values to scale, click to fit · Ruler: Shift-click or Shift-drag · Click to finish · Esc to clear · In merged view, select near an exchange line</div>
      {visibleExchanges.length === 0 && (
        <div className="mb-2 flex h-20 items-center justify-center rounded-xl border border-dashed border-slate-800 text-sm text-slate-500">
          Select at least one OI exchange.
        </div>
      )}
      <div className="screener-chart-grid" data-chart-grid>
        {visibleChartIds.map((id) => (
          <ChartPaneFrame key={id} id={id} label={`${chartLabel(id)} chart`}
            width={layoutPreferences.value.widths[id] ?? 100}
            onWidthChange={(width) => layoutPreferences.setWidth(id, width)}
            dropProps={reordering.getDropProps(id)} orderControls={reordering.renderControls(id)}>
            <DeferredChartPane height={isCollapsed(id) ? 48 : layoutPreferences.value.heights[id]
              ?? (id === 'price' ? 330 : id === 'merged-oi' ? 440 : id.startsWith('cvd:') ? 200 : 145)}>
              {renderPane(id)}
            </DeferredChartPane>
          </ChartPaneFrame>
        ))}
      </div>
    </div>
  )
}
